import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import Swal from "sweetalert2";
import MainLayout from "../../../../components/layout/MainLayout";
import Breadcrumbs from "../../../../components/common/Breadcrumbs";
import LamaForm from "./LamaForm";

import {
    fetchLamaForm,
    updateLamaForm,
} from "../../../../store/slices/lamaFormsSlice";

const EditLama = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { id } = useParams();
    const { state } = useLocation();

    const caseId = state?.caseId ?? null;
    const patientId = state?.patientId ?? null;

    const [formData, setFormData] = useState({
        patientName: "",
        guardianName: "",
        relationWithPatient: "",
        guardianCnic: "",
        reasonForLama: "",
        otherReason: "",
        consultantName: "",
        diagnosis: "",
        date: "",
        time: "",
        prpRemarks: "",
    });

    // 🔹 Map API → Form (snake_case → camelCase)
    const mapApiToForm = (data) => ({
        patientName: data.patient_name ?? "",
        guardianName: data.guardian_name ?? "",
        relationWithPatient: data.relation_with_patient ?? "",
        guardianCnic: data.guardian_cnic ?? "",
        reasonForLama: data.reason_for_lama ?? "",
        otherReason:
            data.reason_for_lama &&
                ![
                    "Financial Constraints",
                    "Family Concerns",
                    "Dissatisfied with Patient Care",
                    "Unsatisfactory Treatment",
                    "Unsatisfactory Administrative Arrangements",
                ].includes(data.reason_for_lama)
                ? data.reason_for_lama
                : "",
        consultantName: data.consultant_name ?? "",
        diagnosis: data.diagnosis ?? "",
        date: data.date ?? "",
        time: data.time ?? "",
        prpRemarks: data.prp_remarks ?? "",
    });

    // 🔹 Map Form → API (camelCase → snake_case)
    const buildPayload = () => ({
        patient_id: patientId,
        case_id: caseId,
        patient_name: formData.patientName,
        guardian_name: formData.guardianName,
        relation_with_patient: formData.relationWithPatient,
        guardian_cnic: formData.guardianCnic,
        reason_for_lama: formData.reasonForLama,
        other_reason: formData.otherReason,
        consultant_name: formData.consultantName,
        diagnosis: formData.diagnosis,
        date: formData.date,
        time: formData.time,
        prp_remarks: formData.prpRemarks,
    });

    // 🔹 Fetch existing record
    useEffect(() => {
        const loadLama = async () => {
            try {
                const response = await dispatch(fetchLamaForm(id)).unwrap();
                setFormData(mapApiToForm(response));
            } catch (error) {
                Swal.fire("Error", "Failed to load LAMA record", "error");
            }
        };

        loadLama();
    }, [dispatch, id]);

    const handleChange = (e) => {
        const { name, value } = e.target;

        // Optional cleanup for "Other"
        if (name === "reasonForLama" && value !== "Other") {
            setFormData((prev) => ({
                ...prev,
                reasonForLama: value,
                otherReason: "",
            }));
            return;
        }

        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            await dispatch(
                updateLamaForm({
                    case_id: caseId,
                    lamaData: buildPayload(),
                })
            ).unwrap();

            Swal.fire({
                title: "Updated!",
                text: "LAMA form updated successfully.",
                icon: "success",
                timer: 2000,
                showConfirmButton: false,
            });

            navigate(`/cases/${caseId}/profile`, {
                state: { caseId, patientId },
            });
        } catch (error) {
            Swal.fire("Error", "Failed to update LAMA form", "error");
        }
    };

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <Breadcrumbs />
                    <Link
                        to={`/optional/lama-form/print/${id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-secondary"
                    >
                        <i className="ri-printer-line me-1"></i> Print
                    </Link>
                </div>

                <div className="card">
                    <div className="card-body">
                        <form onSubmit={handleSubmit}>
                            <LamaForm
                                formData={formData}
                                handleChange={handleChange}
                            />

                            <div className="col-12 d-flex justify-content-end mt-3">
                                <div className="col-md-2 d-flex justify-content-end gap-2">
                                    <button type="submit" className="btn btn-primary px-4">
                                        <i className="ri-edit-2-line me-1"></i>
                                        Update
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default EditLama;
